import React, { useState } from 'react'
import Label from 'components/Label/Label'
import Password from 'components/Form/Password'
import { validatePassword } from 'components/validate'
import {
  Container,
  Content,
  Heading,
  FormWrapper,
  UListWrapper,
  LiDiv,
  Span,
  Wrapper,
} from 'modules/Onboarding/Components/ActivateAccount/SetPasswordPage/style'
import ErrorMessage from 'utils/ErrorMessage'
import { encrypt } from 'utils/encrypt'
import Button from 'components/Button'
import authRequest from 'utils/authRequest'
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons'

const { AUTH_URL } = process.env

const SetPasswordPage = ({
  studentDetails,
  resetPwd,
  updateCurrentState,
  updateStatusPending,
}) => {

  const [pwd, setPwd] = useState({ value: '', message: '', strength: 'Weak' })
  const [pwd2, setPwd2] = useState({ value: '', message: '' })
  const [loading, setLoading] = useState(false)
  const isSubmit = async () => {
    const hasUpperCase = /[A-Z]/.test(pwd?.value);
    const hasLowerCase = /[a-z]/.test(pwd?.value);
    const hasNumber = /\d/.test(pwd?.value);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(pwd?.value);
    const isLongEnough = pwd?.value?.length >= 8;

    if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecialChar || !isLongEnough) {
      setPwd({ ...pwd, message: 'Please meet all password requirements' });
      return;
    }

    if (pwd?.value !== pwd2?.value) {
      setPwd2({ ...pwd2, message: 'Passwords do not match' });
      return;
    }

    setLoading(true);
    try {
      const response = await resetPwd({
        email: studentDetails?.admin?.email,
        password: await encrypt(pwd?.value),
        user_id: studentDetails?.admin?._id,
      });

      if (response === true) {
        await updateStatusPending(studentDetails?.id)
        const responseStatus = await updateCurrentState(studentDetails?.id, 2);
        if (responseStatus === true) {
          const { data, status } = await authRequest.post(`/user/signin`, {
            email: studentDetails?.admin?.email,
            password: await encrypt(pwd?.value),
          });

          if (status >= 200 && status < 300) {
            if (data?.data?.multipleAccounts) {
              // Auto-select the current account for registration flow
              const selectRes = await authRequest.post(`/user/signin/select-account`, {
                sessionToken: data?.data?.sessionToken,
                userId: studentDetails?.admin?._id,
              });
              if (selectRes?.status >= 200 && selectRes?.status < 300) {
                window.open(selectRes?.data?.data?.redirectLink, '_self');
              } else {
                setLoading(false);
                ErrorMessage(selectRes?.data?.message || 'Failed to select account. Please try again.');
              }
            } else {
              window.open(data?.data?.redirectLink, '_self');
            }
          }
        }
      } else {
        setLoading(false);
        ErrorMessage(response?.response?.data?.message);
      }
    } catch (error) {
      setLoading(false);
      ErrorMessage(error?.message || 'An error occurred');
    }
  }

  return (
    <Container alignCenter column height={'917px'}>
      <Wrapper>
        <Content spaceBetween flexStart column>
          <Heading>Set your account password</Heading>
          <FormWrapper flexStart column>
            <Label marginBottom={'0px'}>Password</Label>
            <Password
              marginTop={'0px'}
              width="100%"
              placeholder="＊＊＊＊＊"
              disable={loading}
              onChange={e => {
                const value = e.target.value;
                const hasUpperCase = /[A-Z]/.test(value);
                const hasLowerCase = /[a-z]/.test(value);
                const hasNumber = /\d/.test(value);
                const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);
                const isLongEnough = value?.length >= 8;
                
                let strength = 'Weak';
                if (isLongEnough && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar) {
                  strength = 'Strong';
                } else if (isLongEnough && (hasUpperCase || hasLowerCase || hasNumber || hasSpecialChar)) {
                  strength = 'Medium';
                }
                
                setPwd({ value, message: '', strength });
              }}
              iconRender={visible =>
                visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
              }
            />
          
            
            <Span>{pwd.message ? pwd.message : ''}</Span>
          </FormWrapper>
          <FormWrapper flexStart column>
            <Label marginBottom={'0px'}>Confirm Password</Label>
            <Password
              marginTop={'0px'}
              width="100%"
              placeholder="＊＊＊＊＊＊＊＊"
              disable={loading}
              onChange={e => setPwd2({ value: e.target.value, message: '' })}
              iconRender={visible =>
                visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
              }
            />
            <Span>{pwd2.message ? pwd2.message : ''}</Span>
          </FormWrapper>
          <Button.Primary
            width={'100%'}
            disable={loading}
            onClick={isSubmit}
            text="Submit"
          />
          <div style={{ marginTop: '1px' }}>
          <Label marginBottom={'14px'}>Password Criteria:</Label>
              <div style={{ color: pwd.value?.length >= 8 ? '#52C41A' : '#8C8C8C', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <span style={{ fontSize: '8px' }}>●</span>
                At least 8 characters
              </div>
              <div style={{ color: /[A-Z]/.test(pwd.value) && /[a-z]/.test(pwd.value) ? '#52C41A' : '#8C8C8C', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <span style={{ fontSize: '8px' }}>●</span>
                Uppercase and lowercase character
              </div>
              <div style={{ color: /[!@#$%^&*(),.?":{}|<>]/.test(pwd.value) ? '#52C41A' : '#8C8C8C', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <span style={{ fontSize: '8px' }}>●</span>
                One special character
              </div>
              <div style={{ color: /\d/.test(pwd.value) ? '#52C41A' : '#8C8C8C', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <span style={{ fontSize: '8px' }}>●</span>
                One number
              </div>
              <div style={{ color: pwd.value && pwd2.value && pwd.value === pwd2.value ? '#52C41A' : '#8C8C8C', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <span style={{ fontSize: '8px' }}>●</span>
                Passwords match
              </div>
            </div>

          {/* <Anchor onClick={() => isSubmit()}>Submit</Anchor> */}
        </Content>
      </Wrapper>
    </Container>
  )
}

export default SetPasswordPage
